import * as React from 'react';
import {
  CaretSortIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@radix-ui/react-icons';
import * as SelectPrimitive from '@radix-ui/react-select';

import { cn } from '@/lib/utils';

// Select wraps Radix Root (a state provider, not a DOM node) with an
// immediate-close guard. Radix Root does NOT accept a ref, so this is a
// plain function component. Do not wrap in forwardRef — the inner
// component never consumes the ref and React will log a console warning.
type SelectProps = React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root> & {
  preventImmediateClose?: boolean;
};

const Select: React.FC<SelectProps> = ({
  preventImmediateClose = true,
  ...props
}) => {
  // Track if the select was just opened to prevent immediate closing
  const [justOpened, setJustOpened] = React.useState(false);

  // Handle open state changes
  const handleOpenChange = (open: boolean) => {
    if (open && preventImmediateClose) {
      setJustOpened(true);
      // Reset the flag after a short delay
      setTimeout(() => {
        setJustOpened(false);
      }, 100);
    }

    // Call the original onOpenChange handler if provided
    if (props.onOpenChange) {
      props.onOpenChange(open);
    }
  };

  return (
    <SelectPrimitive.Root
      {...props}
      onOpenChange={(open) => {
        // If we're trying to close the menu and it just opened, prevent it
        if (!open && justOpened && preventImmediateClose) {
          return;
        }
        handleOpenChange(open);
      }}
    />
  );
};
Select.displayName = 'Select';

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = (
  {
    ref,
    className,
    children,
    ...props
  }: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Trigger>>;
  }
) => (<SelectPrimitive.Trigger
  ref={ref}
  className={cn(
    'flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
    className
  )}
  onClick={(e) => {
    // Stop the event from bubbling to prevent immediate closing
    e.stopPropagation();
    
    // Call the original onClick handler if provided
    if (props.onClick) {
      props.onClick(e);
    }
  }}
  {...props}
>
  {children}
  <SelectPrimitive.Icon asChild>
    <CaretSortIcon className="h-4 w-4 opacity-50" />
  </SelectPrimitive.Icon>
</SelectPrimitive.Trigger>);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = (
  {
    ref,
    className,
    ...props
  }: React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton> & {
    ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.ScrollUpButton>>;
  }
) => (<SelectPrimitive.ScrollUpButton
  ref={ref}
  className={cn(
    'flex cursor-default items-center justify-center py-1',
    className
  )}
  {...props}
>
  <ChevronUpIcon />
</SelectPrimitive.ScrollUpButton>);
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = (
  {
    ref,
    className,
    ...props
  }: React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton> & {
    ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.ScrollDownButton>>;
  }
) => (<SelectPrimitive.ScrollDownButton
  ref={ref}
  className={cn(
    'flex cursor-default items-center justify-center py-1',
    className
  )}
  {...props}
>
  <ChevronDownIcon />
</SelectPrimitive.ScrollDownButton>);
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = (
  {
    ref,
    className,
    children,
    position = 'popper',
    ...props
  }: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Content>>;
  }
) => (<SelectPrimitive.Portal>
  <SelectPrimitive.Content
    ref={ref}
    className={cn(
      'relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      position === 'popper' &&
        'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
      className
    )}
    position={position}
    {...props}
  >
    <SelectScrollUpButton />
    <SelectPrimitive.Viewport
      className={cn(
        'p-1',
        position === 'popper' &&
          'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
      )}
    >
      {children}
    </SelectPrimitive.Viewport>
    <SelectScrollDownButton />
  </SelectPrimitive.Content>
</SelectPrimitive.Portal>);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = (
  {
    ref,
    className,
    ...props
  }: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label> & {
    ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Label>>;
  }
) => (<SelectPrimitive.Label
  ref={ref}
  className={cn('px-2 py-1.5 text-sm font-semibold', className)}
  {...props}
/>);
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = (
  {
    ref,
    className,
    children,
    ...props
  }: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Item>>;
  }
) => (<SelectPrimitive.Item
  ref={ref}
  className={cn(
    'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
    className
  )}
  {...props}
>
  <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
    <SelectPrimitive.ItemIndicator>
      <CheckIcon className="h-4 w-4" />
    </SelectPrimitive.ItemIndicator>
  </span>
  <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
</SelectPrimitive.Item>);
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = (
  {
    ref,
    className,
    ...props
  }: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator> & {
    ref?: React.Ref<React.ElementRef<typeof SelectPrimitive.Separator>>;
  }
) => (<SelectPrimitive.Separator
  ref={ref}
  className={cn('-mx-1 my-1 h-px bg-muted', className)}
  {...props}
/>);
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
